import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { FaCheckCircle } from "react-icons/fa";
import {
  addStudents,
  getClasses,
  getStudentsByClassId,
  updateStudent
} from "../services/APIClient";
import "./TeacherAddChildModal.css";

const GRADE_LEVELS = [
  { value: "grade_level1", label: "Junior Infants" },
  { value: "grade_level2", label: "Senior Infants" },
  { value: "grade_level3", label: "1st Class" },
  { value: "grade_level4", label: "2nd Class" },
  { value: "grade_level5", label: "3rd Class" },
  { value: "grade_level6", label: "4th Class" },
  { value: "grade_level7", label: "5th Class" },
  { value: "grade_level8", label: "6th Class" }
];

const DUPLICATE_DECISIONS = {
  same: "same",
  different: "different"
};

const emptySupportDetails = {
  eal: false,
  speechAndLanguage: false,
  asd: false,
  gdd: false,
  note: ""
};

const normaliseName = (name) =>
  String(name || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();

const normaliseText = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ");

const normalisePrefill = (prefill) => ({
  studentName: normaliseText(prefill?.studentName || prefill?.student_name),
  className: normaliseText(prefill?.className || prefill?.class_name),
  supportDetails: {
    eal: Boolean(prefill?.support?.eal),
    speechAndLanguage: Boolean(
      prefill?.support?.speechAndLanguage || prefill?.support?.speech_and_language
    ),
    asd: Boolean(prefill?.support?.asd),
    gdd: Boolean(prefill?.support?.gdd),
    note: normaliseText(prefill?.note)
  }
});

const findExactClassMatch = (classes, className) => {
  if (!className) {
    return null;
  }

  const exactMatches = classes.filter(
    (classItem) => normaliseName(classItem.name) === normaliseName(className)
  );

  return exactMatches.length === 1 ? exactMatches[0] : null;
};

const getFirstName = (name) => (name || "This child").trim().split(" ")[0];
const getChildId = (child) => child?.id || child?._id;
const getClassId = (classItem) => classItem?.id || classItem?._id;

const buildSupportNotePayloads = (note, detailsPayload) => [
  { ...detailsPayload, notes: note },
  { ...detailsPayload, note },
  { ...detailsPayload, supportNote: note }
];

const getGradeBooleanFromClass = (classItem) => {
  const gradeLevel = GRADE_LEVELS.find((grade) => classItem?.[grade.value]);
  return gradeLevel?.value || classItem?.gradeBoolean || "unknown";
};

const getGradeLabelFromClass = (classItem) => {
  const gradeBoolean = getGradeBooleanFromClass(classItem);
  const gradeLevel = GRADE_LEVELS.find((grade) => grade.value === gradeBoolean);
  return gradeLevel?.label || classItem?.grade || "";
};

const getAcademicYearFromClass = (classItem) =>
  classItem?.academicYear ||
  classItem?.academic_year ||
  classItem?.academicyear ||
  classItem?.academicYearLabel ||
  classItem?.yearLabel ||
  classItem?.schoolYear ||
  null;

const normaliseClass = (classItem) => {
  const academicYear = getAcademicYearFromClass(classItem);
  const grade = getGradeLabelFromClass(classItem);
  const displayName = [
    classItem?.name || "Unnamed Class",
    grade,
    academicYear
  ].filter(Boolean).join(" - ");

  return {
    ...classItem,
    id: getClassId(classItem),
    name: classItem?.name || "Unnamed Class",
    academicYear,
    grade,
    displayName
  };
};

const extractAddedChild = (response, fallbackName) => {
  const possibleChildren = [
    ...(Array.isArray(response) ? response : []),
    ...(Array.isArray(response?.students) ? response.students : []),
    ...(Array.isArray(response?.addedStudents) ? response.addedStudents : []),
    ...(Array.isArray(response?.createdStudents) ? response.createdStudents : []),
    ...(Array.isArray(response?.data) ? response.data : [])
  ];
  const possibleChild =
    possibleChildren[0] ||
    response?.student ||
    response?.addedStudent ||
    response?.createdStudent ||
    response;

  if (possibleChild && typeof possibleChild === "object") {
    return {
      ...possibleChild,
      id: getChildId(possibleChild),
      name: possibleChild.name || fallbackName
    };
  }

  return { name: fallbackName };
};

function TeacherAddChildModal({
  show,
  handleClose,
  schoolid,
  preferredClass = null,
  prefill = null,
  onAssignScreener = null,
  embedded = false,
  onWorkflowComplete = null
}) {
  const [stage, setStage] = useState("form");
  const [childName, setChildName] = useState("");
  const [classSearchTerm, setClassSearchTerm] = useState("");
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [duplicateChild, setDuplicateChild] = useState(null);
  const [classStudentsBeforeAdd, setClassStudentsBeforeAdd] = useState([]);
  const [duplicateDecision, setDuplicateDecision] = useState(DUPLICATE_DECISIONS.different);
  const [duplicateDisplayName, setDuplicateDisplayName] = useState("");
  const [addedChild, setAddedChild] = useState(null);
  const [supportDetails, setSupportDetails] = useState(emptySupportDetails);
  const [isLoadingClasses, setIsLoadingClasses] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isSavingDetails, setIsSavingDetails] = useState(false);
  const [error, setError] = useState("");
  const [supportMessage, setSupportMessage] = useState("");

  const trimmedChildName = childName.trim().replace(/\s+/g, " ");
  const trimmedDuplicateDisplayName = duplicateDisplayName.trim().replace(/\s+/g, " ");
  const childFirstName = getFirstName(trimmedChildName || addedChild?.name);
  const normalisedClassSearchTerm = classSearchTerm.trim().toLowerCase();

  useEffect(() => {
    if (!show) {
      return;
    }

    const initialPrefill = normalisePrefill(prefill);

    setStage("form");
    setChildName(initialPrefill.studentName);
    setClassSearchTerm(initialPrefill.className);
    setSelectedClass(null);
    setDuplicateChild(null);
    setClassStudentsBeforeAdd([]);
    setDuplicateDecision(DUPLICATE_DECISIONS.different);
    setDuplicateDisplayName("");
    setAddedChild(null);
    setSupportDetails(initialPrefill.supportDetails);
    setError("");
    setSupportMessage("");
    setIsLoadingClasses(true);

    getClasses()
      .then((classesResponse) => {
        let transformedClasses = (Array.isArray(classesResponse) ? classesResponse : [])
          .map(normaliseClass)
          .sort((classA, classB) => {
            if (classA.academicYear !== classB.academicYear) {
              return classB.academicYear?.localeCompare(classA.academicYear || "") || 0;
            }

            return classA.name.localeCompare(classB.name);
          });
        const normalisedPreferredClass = preferredClass
          ? normaliseClass(preferredClass)
          : null;

        if (normalisedPreferredClass) {
          const matchingPreferredClass = transformedClasses.find((classItem) => {
            const preferredClassId = getClassId(normalisedPreferredClass);

            return (
              (preferredClassId && getClassId(classItem) === preferredClassId) ||
              normaliseName(classItem.name) === normaliseName(normalisedPreferredClass.name)
            );
          });

          if (matchingPreferredClass) {
            setSelectedClass(matchingPreferredClass);
            setClassSearchTerm(matchingPreferredClass.name);
          } else if (normalisedPreferredClass.id) {
            transformedClasses = [normalisedPreferredClass, ...transformedClasses];
            setSelectedClass(normalisedPreferredClass);
            setClassSearchTerm(normalisedPreferredClass.name);
          }
        } else {
          const prefilledClass = findExactClassMatch(
            transformedClasses,
            initialPrefill.className
          );

          if (prefilledClass) {
            setSelectedClass(prefilledClass);
          }
        }

        setClasses(transformedClasses);
      })
      .catch((classesError) => {
        console.error("Error loading teacher classes:", classesError);
        setError("Classes could not be loaded. Please try again.");
        setClasses([]);
      })
      .finally(() => {
        setIsLoadingClasses(false);
      });
  }, [show, preferredClass, prefill]);

  const filteredClasses = useMemo(() => {
    if (!normalisedClassSearchTerm) {
      return classes;
    }

    return classes.filter((classItem) =>
      [classItem.name, classItem.grade, classItem.academicYear, classItem.displayName].some(
        (value) => String(value || "").toLowerCase().includes(normalisedClassSearchTerm)
      )
    );
  }, [classes, normalisedClassSearchTerm]);

  const canAddChild = Boolean(trimmedChildName && selectedClass?.id && !isAdding);
  const addedChildName = addedChild?.name || trimmedChildName;
  const selectedClassName = selectedClass?.name || "the selected class";
  const effectiveSchoolId =
    schoolid ||
    selectedClass?.schoolid ||
    selectedClass?.schoolId ||
    selectedClass?.school?.id;

  const buildNewChildPayload = (nameToAdd = trimmedChildName) => [
    {
      name: nameToAdd,
      gender: undefined,
      age: undefined,
      esl: false,
      sen: false,
      asd: false,
      gdd: false,
      send: false,
      country: undefined,
      currentGrade: undefined,
      isSpecialEd: false
    }
  ];

  const handleClassSearchChange = (event) => {
    const nextSearchTerm = event.target.value;
    setClassSearchTerm(nextSearchTerm);

    if (
      selectedClass &&
      normaliseName(nextSearchTerm) !== normaliseName(selectedClass.name)
    ) {
      setSelectedClass(null);
    }
  };

  const confirmAddedChildInClass = async (
    response,
    studentsBeforeAdd = [],
    nameToConfirm = trimmedChildName
  ) => {
    const responseChild = extractAddedChild(response, nameToConfirm);
    const previousMatchingIds = new Set(
      studentsBeforeAdd
        .filter((student) => normaliseName(student?.name) === normaliseName(nameToConfirm))
        .map(getChildId)
        .filter(Boolean)
    );

    for (const delayMs of [0, 500]) {
      if (delayMs > 0) {
        await new Promise((resolve) => window.setTimeout(resolve, delayMs));
      }

      const refreshedStudents = await getStudentsByClassId(effectiveSchoolId, selectedClass.id);
      const matchingChildren = (Array.isArray(refreshedStudents) ? refreshedStudents : [])
        .filter((student) => normaliseName(student?.name) === normaliseName(nameToConfirm));

      const responseMatch = responseChild.id
        ? matchingChildren.find((student) => getChildId(student) === responseChild.id)
        : null;
      const newMatch = matchingChildren.find((student) => {
        const studentId = getChildId(student);
        return studentId && !previousMatchingIds.has(studentId);
      });

      if (responseMatch || newMatch || (previousMatchingIds.size === 0 && matchingChildren[0])) {
        const confirmedChild = responseMatch || newMatch || matchingChildren[0];

        return {
          ...responseChild,
          ...confirmedChild,
          id: getChildId(confirmedChild) || responseChild.id,
          name: confirmedChild.name || responseChild.name || nameToConfirm
        };
      }
    }

    return null;
  };

  const submitAddChild = async (studentsBeforeAdd = [], nameToAdd = trimmedChildName) => {
    setIsAdding(true);
    setError("");

    try {
      const response = await addStudents(selectedClass.id, buildNewChildPayload(nameToAdd));

      if (!response) {
        setError("The child could not be added. Please try again.");
        setStage("form");
        return;
      }

      const confirmedChild = await confirmAddedChildInClass(response, studentsBeforeAdd, nameToAdd);

      if (!confirmedChild?.id) {
        setError(
          "The child was submitted, but could not be confirmed in this class yet. Please refresh the Teacher Dashboard before trying again."
        );
        setStage("form");
        return;
      }

      setAddedChild(confirmedChild);
      setSupportMessage("");
      setStage("success");
    } catch (addError) {
      console.error("Error adding child:", addError);
      setError("The child could not be added. Please try again.");
      setStage("form");
    } finally {
      setIsAdding(false);
    }
  };

  const handleAddChild = async () => {
    if (!canAddChild) {
      return;
    }

    if (!effectiveSchoolId) {
      setError("Your school could not be confirmed. Please refresh and try again.");
      return;
    }

    setIsAdding(true);
    setError("");
    let classStudents = [];

    try {
      classStudents = await getStudentsByClassId(effectiveSchoolId, selectedClass.id);
      setClassStudentsBeforeAdd(Array.isArray(classStudents) ? classStudents : []);
      const matchingChild = (Array.isArray(classStudents) ? classStudents : []).find(
        (student) => normaliseName(student?.name) === normaliseName(trimmedChildName)
      );

      if (matchingChild) {
        setDuplicateChild(matchingChild);
        setDuplicateDecision(DUPLICATE_DECISIONS.different);
        setDuplicateDisplayName("");
        setStage("duplicate");
        return;
      }
    } catch (duplicateError) {
      console.error("Error checking class children:", duplicateError);
      setError("This class could not be checked. Please try again.");
      return;
    } finally {
      setIsAdding(false);
    }

    await submitAddChild(classStudents);
  };

  const handleDuplicateConfirm = async () => {
    if (duplicateDecision === DUPLICATE_DECISIONS.same) {
      setStage("form");
      setError(`${trimmedChildName} is already in ${selectedClassName}.`);
      return;
    }

    if (!trimmedDuplicateDisplayName) {
      setError("Enter how this child should appear in the class list.");
      return;
    }

    await submitAddChild(classStudentsBeforeAdd, trimmedDuplicateDisplayName);
  };

  const handleSupportDetailChange = (field, value) => {
    setSupportDetails((details) => ({
      ...details,
      [field]: value
    }));
  };

  const saveSupportNote = async (studentId, note, detailsPayload) => {
    let lastError = null;

    for (const notePayload of buildSupportNotePayloads(note, detailsPayload)) {
      try {
        await updateStudent(studentId, notePayload);
        return;
      } catch (noteError) {
        lastError = noteError;
      }
    }

    throw lastError;
  };

  const handleSaveSupportDetails = async () => {
    if (!addedChild?.id) {
      setError("Support details could not be saved for this child yet.");
      return;
    }

    setIsSavingDetails(true);
    setError("");

    const supportNote = supportDetails.note.trim();
    const detailsPayload = {
      esl: supportDetails.eal,
      sen: supportDetails.speechAndLanguage,
      asd: supportDetails.asd,
      gdd: supportDetails.gdd
    };

    try {
      await updateStudent(addedChild.id, detailsPayload);

      if (supportNote) {
        try {
          await saveSupportNote(addedChild.id, supportNote, detailsPayload);
        } catch (noteError) {
          console.error("Error saving support note:", noteError);
          setError("Support tags were saved, but the note could not be saved. Please try the note again.");
          return;
        }
      }

      setSupportMessage("Support details saved.");
      setStage("success");
    } catch (supportError) {
      console.error("Error saving support details:", supportError);
      setError("Support details could not be saved. Please try again.");
    } finally {
      setIsSavingDetails(false);
    }
  };

  const handleAddAnotherChild = () => {
    setStage("form");
    setChildName("");
    setDuplicateChild(null);
    setClassStudentsBeforeAdd([]);
    setDuplicateDecision(DUPLICATE_DECISIONS.different);
    setDuplicateDisplayName("");
    setAddedChild(null);
    setSupportDetails(emptySupportDetails);
    setError("");
    setSupportMessage("");
  };

  const handleDone = () => {
    if (onWorkflowComplete) {
      onWorkflowComplete({
        reference: addedChild?.id || "",
        message: "Child added."
      });
      return;
    }

    handleClose();
  };

  const handleModalHide = () => {
    if (stage === "success" || stage === "support") {
      handleDone();
      return;
    }

    handleClose();
  };

  const renderClassResults = () => {
    if (isLoadingClasses) {
      return (
        <div className="add-child-search-state">
          <Spinner animation="border" size="sm" />
          <span>Loading classes...</span>
        </div>
      );
    }

    if (selectedClass) {
      return (
        <button
          type="button"
          key={selectedClass.id}
          className="add-child-class-result selected"
          onClick={() => {
            setSelectedClass(null);
            setDuplicateChild(null);
            setClassStudentsBeforeAdd([]);
            setDuplicateDisplayName("");
            setError("");
          }}
          aria-label={`Select ${selectedClass.displayName}`}
          aria-pressed
        >
          <span>
            <strong>{selectedClass.name}</strong>
            <span>{[selectedClass.grade, selectedClass.academicYear].filter(Boolean).join(" - ")}</span>
          </span>
          <strong>Selected</strong>
        </button>
      );
    }

    if (filteredClasses.length === 0) {
      return <p className="add-child-muted">No matching classes found.</p>;
    }

    return filteredClasses.map((classItem) => {
      const isSelected = selectedClass?.id === classItem.id;

      return (
        <button
          type="button"
          key={classItem.id}
          className={`add-child-class-result${isSelected ? " selected" : ""}`}
          onClick={() => {
            setSelectedClass(classItem);
            setDuplicateChild(null);
            setClassStudentsBeforeAdd([]);
            setDuplicateDisplayName("");
            setError("");
          }}
          aria-label={`Select ${classItem.displayName}`}
          aria-pressed={isSelected}
        >
          <span>
            <strong>{classItem.name}</strong>
            <span>{[classItem.grade, classItem.academicYear].filter(Boolean).join(" - ")}</span>
          </span>
          {isSelected && <strong>Selected</strong>}
        </button>
      );
    });
  };

  const renderForm = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title>Add Child</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="add-child-intro">Add one missing child to an existing class.</p>
        {error && <Alert variant="danger">{error}</Alert>}

        <div className="add-child-form-grid">
          <Form.Group controlId="addChildName">
            <Form.Label>Child name</Form.Label>
            <Form.Control
              type="text"
              value={childName}
              onChange={(event) => {
                setChildName(event.target.value);
                setDuplicateChild(null);
                setClassStudentsBeforeAdd([]);
                setDuplicateDisplayName("");
              }}
              placeholder="Mary Murphy"
              aria-label="Child name"
            />
          </Form.Group>

          <Form.Group controlId="addChildClassSearch">
            <Form.Label>Search class</Form.Label>
            <Form.Control
              type="search"
              value={classSearchTerm}
              onChange={handleClassSearchChange}
              placeholder="24B"
              aria-label="Search class"
            />
          </Form.Group>
        </div>

        <p className="add-child-results-note">
          {normalisedClassSearchTerm
            ? `Showing matching classes for ${classSearchTerm.trim()}.`
            : "Search or select one of your classes."}
        </p>

        <div className="add-child-class-results" aria-label="Matching classes">
          {renderClassResults()}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <span className="add-child-footer-note">
          {selectedClass ? `${selectedClass.name} selected` : ""}
        </span>
        <Button variant="outline-secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="primary" disabled={!canAddChild} onClick={handleAddChild}>
          {isAdding ? "Adding..." : "Add child"}
        </Button>
      </Modal.Footer>
    </>
  );

  const renderDuplicate = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title>Check Child</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="add-child-intro">This appears only when the name needs a decision.</p>
        {error && <Alert variant="danger">{error}</Alert>}
        <div className="add-child-duplicate-summary">
          <strong>{duplicateChild?.name || trimmedChildName} is already in {selectedClassName}.</strong>
          <span>Is this the same child, or a different child with the same name?</span>
        </div>
        <div className="add-child-duplicate-options">
          <label
            className={`add-child-option${duplicateDecision === DUPLICATE_DECISIONS.same ? " selected" : ""}`}
          >
            <Form.Check
              type="radio"
              name="duplicateChildDecision"
              checked={duplicateDecision === DUPLICATE_DECISIONS.same}
              onChange={() => setDuplicateDecision(DUPLICATE_DECISIONS.same)}
              aria-label={`I did not realise ${childFirstName} was already in the class`}
            />
            <span>
              <strong>I didn't realise {childFirstName} was already in the class.</strong>
              <span>{childFirstName} will not be added again.</span>
            </span>
          </label>
          <div
            className={`add-child-option${duplicateDecision === DUPLICATE_DECISIONS.different ? " selected" : ""}`}
          >
            <Form.Check
              type="radio"
              name="duplicateChildDecision"
              checked={duplicateDecision === DUPLICATE_DECISIONS.different}
              onChange={() => setDuplicateDecision(DUPLICATE_DECISIONS.different)}
              aria-label={`This is a different ${trimmedChildName}`}
            />
            <span>
              <strong>This is a different {trimmedChildName}.</strong>
              <span>Add a new {trimmedChildName} to {selectedClassName}.</span>
              {duplicateDecision === DUPLICATE_DECISIONS.different && (
                <Form.Group
                  className="add-child-duplicate-display-name"
                  controlId="duplicateDisplayName"
                >
                  <Form.Label>How should this child appear in the class list?</Form.Label>
                  <Form.Control
                    type="text"
                    value={duplicateDisplayName}
                    onChange={(event) => setDuplicateDisplayName(event.target.value)}
                    placeholder="Mary C Murphy"
                    aria-label="Class list display name"
                  />
                  <Form.Text>
                    Use a middle initial or another name you use in class.
                  </Form.Text>
                </Form.Group>
              )}
            </span>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <span className="add-child-footer-note">Only {childFirstName} needs checking</span>
        <Button variant="outline-secondary" onClick={() => setStage("form")}>
          Back
        </Button>
        <Button
          variant="primary"
          disabled={
            isAdding ||
            (
              duplicateDecision === DUPLICATE_DECISIONS.different &&
              !trimmedDuplicateDisplayName
            )
          }
          onClick={handleDuplicateConfirm}
        >
          {isAdding
            ? "Adding..."
            : duplicateDecision === DUPLICATE_DECISIONS.same
              ? "Done"
              : "Add child"}
        </Button>
      </Modal.Footer>
    </>
  );

  const renderSuccess = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title>Child Added</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="add-child-intro">
          {addedChildName} now appears in {selectedClassName}.
        </p>
        {supportMessage && <Alert variant="success">{supportMessage}</Alert>}
        {error && <Alert variant="danger">{error}</Alert>}
        <Alert variant="success" className="add-child-success">
          <FaCheckCircle aria-hidden="true" />
          <span>
            <strong>{addedChildName} has been added to {selectedClassName}.</strong>
            <span>{childFirstName} now appears in this class list.</span>
          </span>
        </Alert>
      </Modal.Body>
      <Modal.Footer>
        <span className="add-child-footer-note">
          {embedded ? "Done returns to SENSAI." : "What would you like to do next?"}
        </span>
        <Button variant="outline-secondary" onClick={() => setStage("support")}>
          Add support details
        </Button>
        <Button variant="outline-secondary" onClick={handleAddAnotherChild}>
          Add another child
        </Button>
        {onAssignScreener && (
          <Button
            variant="outline-secondary"
            onClick={() => onAssignScreener({ className: selectedClassName })}
          >
            Assign screener
          </Button>
        )}
        <Button variant="primary" onClick={handleDone}>
          Done
        </Button>
      </Modal.Footer>
    </>
  );

  const renderSupportDetails = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title>Add Support Details</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="add-child-intro">
          {addedChildName} has been added to {selectedClassName}.
        </p>
        {error && <Alert variant="danger">{error}</Alert>}
        <Form.Group className="mb-3">
          <Form.Label>Do you want to add support details for {childFirstName}?</Form.Label>
          <div className="add-child-support-options">
            <label>
              <Form.Check
                type="checkbox"
                checked={supportDetails.eal}
                onChange={(event) => handleSupportDetailChange("eal", event.target.checked)}
                aria-label="English as an additional language"
              />
              <span>English as an additional language</span>
            </label>
            <label>
              <Form.Check
                type="checkbox"
                checked={supportDetails.speechAndLanguage}
                onChange={(event) =>
                  handleSupportDetailChange("speechAndLanguage", event.target.checked)
                }
                aria-label="Speech and language"
              />
              <span>Speech and language</span>
            </label>
            <label>
              <Form.Check
                type="checkbox"
                checked={supportDetails.asd}
                onChange={(event) => handleSupportDetailChange("asd", event.target.checked)}
                aria-label="ASD"
              />
              <span>ASD</span>
            </label>
            <label>
              <Form.Check
                type="checkbox"
                checked={supportDetails.gdd}
                onChange={(event) => handleSupportDetailChange("gdd", event.target.checked)}
                aria-label="GDD"
              />
              <span>GDD</span>
            </label>
          </div>
        </Form.Group>
        <Form.Group controlId="addChildSupportNote">
          <Form.Label>Note</Form.Label>
          <Form.Control
            as="textarea"
            rows={4}
            value={supportDetails.note}
            onChange={(event) => handleSupportDetailChange("note", event.target.value)}
            placeholder="New to the school. Parent mentioned English support may be helpful."
            aria-label="Support note"
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <span className="add-child-footer-note">
          {addedChildName} has been added to {selectedClassName}.
        </span>
        <Button variant="outline-secondary" onClick={() => setStage("success")}>
          Back
        </Button>
        <Button variant="primary" disabled={isSavingDetails} onClick={handleSaveSupportDetails}>
          {isSavingDetails ? "Saving..." : "Save details"}
        </Button>
      </Modal.Footer>
    </>
  );

  return (
    <Modal
      show={show}
      onHide={handleModalHide}
      centered
      backdrop="static"
      className="teacher-add-child-modal"
    >
      {stage === "form" && renderForm()}
      {stage === "duplicate" && renderDuplicate()}
      {stage === "success" && renderSuccess()}
      {stage === "support" && renderSupportDetails()}
    </Modal>
  );
}

export default TeacherAddChildModal;
